import {
  IOverlapSchedule,
  SaveAction,
} from "../../../components/Posts/PostForm/types";
import type { PostStatesType } from "../../../constants/PostsView";
import type { Platform } from "../../../features/post/format-post";
import type { CreatePostRequestType } from "../../../features/post/types";
import type { AccountPlatformType, AccountType } from "../accounts";
import type { IgPreflightResult } from "../../../api/post";
import type { CoverFrame } from "../media";

export declare type PostID = string;

/**
 * Instagram per-post options, mirroring `InstagramPostOptions` in
 * `apps/schedugram-server/src/types/post.ts`. The server derives its Mongoose
 * sub-schema and its normaliser from one spec table keyed by that type, so this
 * declaration is the only remaining layer that can drop a value without saying
 * so. A key missing here cannot be written by the composer at all.
 */
export type InstagramPostOptions = {
  shareToFeed?: boolean | null;
  audioName?: string;
  audioConfiguration?: {
    audioId: string;
    audioVolume?: number;
    videoVolume?: number;
  };
  trialReel?: { graduationStrategy: "MANUAL" | "SS_PERFORMANCE" };
  thumbOffsetMs?: number;
  isAiGenerated?: boolean;
  /**
   * Display only, and never sent to Meta. See the server-side declaration for
   * why it exists: `audioConfiguration` keeps the `audioId` alone, so the
   * chosen track could not be named after a reload.
   */
  audioMeta?: {
    title?: string;
    artist?: string;
    artworkUrl?: string;
  };
};

export type PostMediaFile = {
  isVideo?: boolean;
  _id?: string;
  originalUrl?: string;
  origUrl?: string;
  url: string;
  thumbnailUrl: string;
  /** Response-only imgproxy rendition for UI thumbnail slots. */
  displayThumbnailUrl?: string;
  fileName?: string;
  type?: string;
  meta?: {
    duration?: number;
    width?: number;
    height?: number;
    size?: number;
    byteSize?: number;
    format?: {
      duration?: number;
      type?: string;
      mime?: string;
    };
    isVideo?: boolean;
    isImage?: boolean;
  };
  altText?: string;
  asset_id?: string;
  lowQualityVideo?: {
    bucket: string;
    fileSize: number;
    frameRate: number;
    height: number;
    key: string;
    url: string;
    width: number;
  };
  transcodedVideo?: {
    bucket: string;
    key: string;
    url: string;
  };
  description?: string;
  isDocument?: boolean;
  size?: number;
  coverFrames?: CoverFrame[];
  /**
   * Per-slide carousel tags. The publish path reads these in preference to the
   * post-level arrays once any slide carries them, so the two are alternatives
   * rather than additions.
   */
  tags?: unknown[];
  productTags?: unknown[];
};

export type MediaUrlInfo = Pick<
  PostMediaFile,
  "url" | "meta" | "isVideo" | "type" | "thumbnailUrl"
>;

export enum PostStates {
  draft = "draft",
  schedule = "schedule",
  queue = "queue",
  archived = "archived",
}

export type ScheduledPost = {
  state: PostStates.schedule;
  when: string | null;
};
export type QueuedPost = {
  state: PostStates.queue;
  when: string | null;
};
type ArchivedPost = {
  state: PostStates.archived;
  when: string | null;
};
type DraftedPost = {
  state: PostStates.draft;
};
type PostState = ScheduledPost | QueuedPost | DraftedPost | ArchivedPost;

export enum PlatformTypes {
  instagram = "IG",
  facebook = "FB",
  twitter = "TW",
  linkedin = "LI",
  googlemybusiness = "GMB",
  cluster = "LINKED",
  youtube = "YT",
  pinterest = "PI",
  snapchat = "SC",
  tiktok = "TT",
  other = "OT",
  threads = "TH",
}

export type InstagramPost = {
  platformType: PlatformTypes.instagram;
  firstcomment?: string;
  location?: unknown;
  tags: unknown[];
  productTags: unknown[];
  isStory: boolean;
  storyType?: "story" | "reel" | "igtv" | null;
  storyUrl?: string;
  storyLabel?: string;
  storyStickerHeight?: number;
  publishStoryManually?: boolean;
};

export type FacebookPost = {
  platformType: PlatformTypes.facebook;
  location?: string;
};

export type TwitterPost = {
  platformType: PlatformTypes.twitter;
};

export type SnapchatPost = {
  platformType: PlatformTypes.snapchat;
};

export type OtherPost = {
  platformType: PlatformTypes.other;
};

export type LinkedInPost = {
  platformType: PlatformTypes.linkedin;
  liTargetEntities?: {
    [key: string]: { label: string }[];
  };
};

export type YoutubePost = {
  platformType: PlatformTypes.youtube;
  ytTitle?: string;
  ytPrivacy?: string;
  ytCategoryId?: number | string;
  ytTags?: string | null;
};

export type TiktokPost = {
  thumbnailUrl?: string;
  videoCoverImageTimestamp?: number;
  videoCoverPreview: string | null;
  platformType: PlatformTypes.tiktok;
  postOptions: TTPostOptions;
};

export interface TTPostOptions {
  isNotification?: boolean;
  allowComments?: boolean;
  allowStitch?: boolean;
  allowDuet?: boolean;
  title?: string;
  autoAddMusic?: boolean;
  privacyLevel?: TiktokPrivacyLevel;
  photoCoverIndex?: number;
}

export type TiktokPrivacyLevel =
  | "PUBLIC_TO_EVERYONE"
  | "MUTUAL_FOLLOW_FRIENDS"
  | "FOLLOWER_OF_CREATOR"
  | "SELF_ONLY";

export type ThreadsPost = {
  platformType: PlatformTypes.threads;
  postOptions: {
    isNotification?: boolean;
    replyControl: string;
  };
};
export type GoogleMyBusinessPost = {
  platformType: PlatformTypes.googlemybusiness;
  gmbTopicType?: string;
  gmbEvent?: {
    title?: string;
    startDate?: Date;
    endDate?: Date;
  };
  gmbOffer?: object;
  gmbCallToAction?: {
    actionType?: string | null;
  } & Record<string, unknown>;
};

export type PinterestPost = {
  platformType: PlatformTypes.pinterest;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  piBoards?: any[];
  piSectionId?: string;
  piTitle?: string;
};

export type ClusterPost = {
  platformType: PlatformTypes.cluster;
  firstcomment: string;
  location?: string;
  tags?: unknown[];
  productTags?: unknown[];
  gmbTopicType?: string;
  gmbEvent?: object;
  gmbOffer?: object;
  gmbCallToAction?: {
    actionType?: string | null;
  } & Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  piBoards?: any[];
  piSectionId?: string;
  piTitle?: string;
};

export type PostByPlatformType =
  | InstagramPost
  | FacebookPost
  | TwitterPost
  | LinkedInPost
  | GoogleMyBusinessPost
  | ClusterPost
  | YoutubePost
  | PinterestPost
  | TiktokPost
  | ThreadsPost
  | SnapchatPost
  | OtherPost;

export type PostType =
  | "image"
  | "video"
  | "text"
  | "multiMedia"
  | "story"
  | "fb-reel"
  | "document"
  | "reel";

export type PostOptions = TTPostOptions;
export type PostBoostData = {
  campaignId?: string;
  adId?: string;
};

export type Post = {
  _id: string;
  accountId?: string;
  accountIds: string[];
  labelIds: string[];
  mediaFiles: MediaFile[];
  // deprecated, use mediaFiles instead
  media?: MediaFile[] | null;
  type: PostType;
  caption?: string;
  channel?: string;
  fileName?: string | null;
  redirectUrl?: string | null;
  url?: string | null;
  isVideo?: boolean;
  queued?: boolean;
  postStatusKey?: string | null;
  postedBy?: string;
  createdBy?: string;
  createdAt?: string;
  originalUrl?: string;
  origUrl?: string | null;
  tags?: unknown[];
  productTags?: unknown[];
  accounts: AccountType[];
  updatedBy?: string;
  updatedAt?: string;
  thumbnailUrl?: string | null;
  /** Response-only imgproxy rendition; never use this for publishing. */
  displayThumbnailUrl?: string | null;
  meta?: {
    height?: number;
    width?: number;
    size?: number;
    pages?: number;
    format?: {
      duration?: number;
      type?: string;
      mime?: string;
    };
  };
  altText?: string;
  when?: string | Date | null | moment.MomentInput;
  schedule?: {
    accountId?: string;
    accountIds: string[];
    channel?: string;
  };
  firstcomment?: string;
  isNow?: boolean;
  isStory?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storyUrl?: any;
  storyLabel?: string;
  storyStickerHeight?: number;
  location?: object;
  storyType?: InstagramPost["storyType"];
  newPost?: boolean;
  nextRunAt?: Date | moment.MomentInput;
  lastRunAt?: Date;
  scheduledFor?: Date;
  /** is used:
   * to determine on opening Post Create modal
   */
  postType?: "draft" | "schedule" | "queue" | "archive" | "archived";
  pushNotificationMeta?: NotificationPerson[];
  isPosting?: boolean;
  isQueued?: boolean;
  isSuccess?: boolean;
  isCanceled?: boolean;
  isPotentialError?: boolean;
  /** Published, but Meta refused this shop's product tags, so they were dropped. */
  igProductTagsDropped?: boolean;
  /** Published, but Meta no longer recognised the tagged location, so it was dropped. */
  igLocationDropped?: boolean;
  platformPostId?: string | null;
  postOptions?: PostOptions;
  videoCoverImageTimestamp?: number;
  videoCoverType?: "uploaded" | "frame_selected" | "platform_default";
  collaboratingIgUsers?: IgCollabPerson[];
  past?: boolean;
  plannerAccount?: AccountType;
  originalPostType?: string;
  id?: string;
  isDocument?: boolean;
  publishStoryManually?: boolean;
  videoPostType?: "video" | "reel" | null;
  /**
   * Instagram-only per-post options. `shareToFeed` true puts the reel in the
   * Feed and Reels tabs, false confines it to the Reels tab.
   */
  instagram?: InstagramPostOptions | null;
  isFbReel?: boolean;
  accountName?: string;
  unlinked?: boolean;
  videoCoverPreview?: string | null;
  userDisplayReason?: string;
  failedAt?: Date;
  /** mostly use for tracking only, but looks like it's never set */
  originalPostState?: PostStatesType;
  /** deprecated, no longer used */
  postState?: PostStatesType;
  platforms?: Array<
    | FormPlatformType
    | {
        platformType: FormPlatformType;
        // other platform-specific fields
      }
  >;
  postClusterId?: string;
  ideaId?: string;
  failReason?: string;
  boostData?: PostBoostData;
  /** Client-only original cover URL used by "Revert to original". */
  videoCoverOriginal?: string | null;
  /** Client-only baseline metadata used by "Revert to original". */
  videoCoverOriginalTimestamp?: number;
  /** Client-only baseline metadata used by "Revert to original". */
  videoCoverOriginalType?: string;
  /** Client-only provenance for a cover selected from the media library. */
  videoCoverPreviewSource?: "media_library";
} & PostState &
  PostByPlatformType;

export interface LoadedDraft {
  checked?: boolean;
  _id: string;
  id?: string;
  schedule: Post & {
    unreadMessageCount?: string;
    preview_enabled?: boolean;
  };
  accountIds: string[];
}

export type PlatformPost<P extends { platformType: PlatformTypes }> = Post & P;
export type StatePost<S extends { state: PostStates }> = Post & S;
export type DefinedPost<
  P extends { platformType: PlatformTypes },
  S extends { state: PostStates },
> = Post & P & S;

type MediaFile = {
  isVideo?: boolean;
  originalUrl?: string;
  origUrl?: string;
  url: string;
  thumbnailUrl: string;
  /** Response-only imgproxy rendition for UI thumbnail slots. */
  displayThumbnailUrl?: string;
  lowQualityVideo?: {
    bucket: string;
    fileSize: number;
    frameRate: number;
    height: number;
    key: string;
    url: string;
    width: number;
  };
  fileName?: string | null;
  type?: string;
  meta?: unknown;
  altText?: string;
  isDocument?: boolean;
  description?: string;
  tags?: unknown[];
  productTags?: unknown[];
};

export type NotificationPerson = {
  _id?: string;
  name?: string;
  notified?: "email" | "mobile";
  delivered?: boolean;
  opened?: boolean;
  failed?: boolean;
  published?: boolean;
};

export type IgCollabPerson = {
  username: string;
  profilePictureUrl: string;
  _id?: string;
};

export type FormValidationErrors = Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { errors: string[] | Record<string, any> }
>;
export type FormValidationWarnings = Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { warnings: string[] | Record<string, any> }
>;
export interface PostFormState {
  post: Post;
  overlapSchedule: IOverlapSchedule;
  /**
   * How the server says this draft would publish, per account, from
   * `POST /instagram-lookups/preflight`.
   *
   * Undefined until the preflight answers, and undefined keeps every phone-era
   * limit in force, which is the safe direction: better to hold a customer to a
   * limit that no longer applies than to offer them a chain the publish path
   * would refuse.
   */
  igPreflight?: IgPreflightResult;
  /**
   * SKE-4733. The last scheduling-time overlap check failed to run, so the form
   * carries a non-blocking warning until a check succeeds.
   */
  overlapCheckFailed?: boolean;
  originalPost: Post;
  originalPostState: PostStatesType;
  postState: PostStatesType;
  platformFields: PlatformFields;
  accounts: AccountType[];
  platforms: Platform[];
  view: {
    selectedPlatform: FormPlatformType;
    visibleFields: string[];
    igAudioPickerOpen?: boolean;
  };
  validation: {
    errors: FormValidationErrors;
    warnings: FormValidationWarnings;
  };
  results?: {
    originalPost: Post;
    newPosts: Post[];
  } | null;
  requestType?: CreatePostRequestType;
  requestedPost: Post;
  requestedId: number;
  formSaveData?: {
    postState: PostStatesType;
    postIds: string[];
  };
  isDirty?: boolean;
  accountSelectionDisabled?: boolean;
  coverPreviewUrl?: string | null;
  requestFormTitle?: string;
  isLazyLoaded?: boolean; // true if form deferred until selection
  isInitialized?: boolean; // true if heavy resources loaded
  userTimezone?: string;
}
export interface PostForm extends PostFormState {
  postFormValue: Post;
  errors: FormValidationErrors | undefined;
  warnings: FormValidationWarnings | undefined;
  newPostState: PostStatesType;
  formAccounts: AccountType[];
  updateFields: (newValueMap: unknown) => void;
  updateField: (field: string, value: unknown) => void;
  saveDisabled: boolean;
  onSubmit: (saveAction: SaveAction | undefined) => void;
  platformPost: Post;
  selectedPlatform: AccountPlatformType;
  visibleFields: string[];
  igAudioPickerOpen: boolean;
  setIgAudioPickerOpen: (open: boolean) => void;
  channelId: string;
  isSubmitting: boolean;
  isSummary?: boolean;
}
interface PlatformField {
  unlinked?: boolean;
}

export type FormPlatformType = AccountPlatformType | "LINKED";
export type PlatformFields = Partial<Record<FormPlatformType, PlatformField>>;
